import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/ui/DataTable';
import { canAccessProviderAdmin } from './providerAdminAccess';

const ADMISSION_PROFILE_LABELS = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
};

function admissionProfileLabel(status) {
  if (status == null || status === '') return 'None';
  const key = String(status).toUpperCase();
  return ADMISSION_PROFILE_LABELS[key] || String(status);
}

function isForbidden(error) {
  return error?.status === 403;
}

const MANDATE_LABELS = {
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  REVOKED: 'Revoked',
};

function custodyMandateLabel(row) {
  const status = row?.custody_mandate_status;
  if (status == null || status === '') {
    return row?.custodian_organization_id ? 'Missing' : '—';
  }
  const key = String(status).toUpperCase();
  const label = MANDATE_LABELS[key] || String(status);
  const principal = row?.custody_mandate?.principal;
  return key === 'ACTIVE' && principal ? `${label} · ${principal}` : label;
}

export function ProgramsPage() {
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mandateBusyId, setMandateBusyId] = useState('');

  const allowed = canAccessProviderAdmin(session);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerAdminApi.listProviderPrograms({ page: 1, pageSize: 100 });
      setForbidden(false);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setItems([]);
      if (isForbidden(err)) {
        setForbidden(true);
        setError(null);
      } else {
        setForbidden(false);
        setError(err.message || 'Failed to load programs');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) {
      load();
    } else {
      setLoading(false);
    }
  }, [allowed, load]);

  async function issueMandate(row) {
    if (!row?.id || !row.custodian_organization_id) return;
    setMandateBusyId(row.id);
    setError(null);
    try {
      await providerAdminApi.createProgramCustodyMandate(row.id, {
        principal: row.custodian_organization_name || 'Custodian',
        mandate_reference: `MANDATE-${row.code || row.id}`,
        activate: true,
      });
      await load();
    } catch (err) {
      setError(err.message || 'Unable to issue a custody mandate.');
    } finally {
      setMandateBusyId('');
    }
  }

  if (!allowed || forbidden) {
    return (
      <p className="text-sm text-eorig-muted">
        Provider Admin access is required to view programs.
      </p>
    );
  }

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'code', header: 'Code' },
    { key: 'organization_name', header: 'Originator' },
    {
      key: 'custodian_organization_name',
      header: 'Custodian',
      render: (row) => row.custodian_organization_name || '—',
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (row) => (row.is_active ? 'Yes' : 'No'),
    },
    {
      key: 'admission_profile_status',
      header: 'Admission profile',
      render: (row) => admissionProfileLabel(row.admission_profile_status),
    },
    {
      key: 'custody_mandate_status',
      header: 'Custody mandate',
      render: (row) => {
        const missing = Boolean(row.custodian_organization_id) && !row.custody_mandate_status;
        return (
          <div className="flex flex-wrap items-center gap-2">
            <span>{custodyMandateLabel(row)}</span>
            {missing ? (
              <button
                type="button"
                className="rounded-full border border-[#003A70] px-2 py-0.5 text-[11px] font-semibold text-[#003A70] hover:bg-[#003A70] hover:text-white disabled:opacity-50"
                disabled={mandateBusyId === row.id}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  issueMandate(row);
                }}
              >
                {mandateBusyId === row.id ? 'Issuing…' : 'Issue mandate'}
              </button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-eorig-muted">
          Programs belong to an originator in this provider&apos;s scope.
        </p>
        <Link
          to="/provider/admin/programs/new"
          className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white"
        >
          Add program
        </Link>
      </div>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading programs…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted">
          No programs for originators in this provider&apos;s scope.
        </div>
      ) : (
        <DataTable columns={columns} rows={items} rowKey={(row) => row.id} />
      )}
    </div>
  );
}
